import { uniqBy, keyBy, omit } from "lodash";
import { isStoreImportProduct } from "../../helpers/manualProductCatalog";

import {
  REQUEST_AND_RESET_PRODUCTS_SUCCESS,
  REQUEST_AND_RESET_PRODUCTS_FAILURE,
  REQUEST_PRODUCTS_SUCCESS,
  REQUEST_PRODUCTS_FAILURE,
  // REQUEST_PRODUCTS_DETAILS_SUCCESS,
  REQUEST_PRODUCTS_DETAILS_FAILURE,
  REQUEST_MANUAL_PRODUCT_SUCCESS,
  UPDATE_MANUAL_PRODUCT_NAME_SUCCESS,
  REQUEST_BLANK_PRODUCT_COLORS_SUCCESS,
  REQUEST_PRODUCTS_COUNT_SUCCESS,
  REQUEST_PRODUCTS_COUNT_FAILURE,
  SAVE_LISTING_TEMPLATE_SUCCESS,
  GET_LISTING_TEMPLATE_SUCCESS,
  DELETE_LISTING_TEMPLATE_SUCCESS,
  SAVE_PRODUCT_MAPPING_SUCCESS,
  BULK_IGNORE_PRODUCTS_SUCCESS,
  SET_BULK_IGNORE_JOB,
  CLEAR_BULK_IGNORE_JOB,
  SET_MOVE_ALL_TO_APP_LOCATION_JOB,
  CLEAR_MOVE_ALL_TO_APP_LOCATION_JOB,
  SET_PRODUCT_CREATE_JOB,
  CLEAR_PRODUCT_CREATE_JOB,
  PATCH_MANUAL_PRODUCT,
  DELETE_PRODUCTS_SUCCESS,
  DELETE_PRODUCTS_FAILURE,
  REMOVE_LISTINGS_SUCCESS,
  UPDATE_PREVIEW_IMAGES,
  UPDATE_CANVAS_IMAGES,
  SET_SELECTED_FILTER_FOR_LISTING,
  SET_LISTING_LAST_ID,
} from "../../constants/actionTypes";

const INITIAL_STATE = {
  products: [],
  manualProducts: [],
  manualProductCatalogUids: [],
  colors: {},
  counts: {},
  templates: {},
  previewImages: {},
  selectedFilter: "",
  lastId: "",
  bulkIgnoreJob: null,
  moveAllToAppLocationJob: null,
  productCreateJob: null,
};

const ACTIVE_CREATE_JOB_STATUSES = new Set([
  "awaiting_uploads",
  "uploading",
  "queued",
  "validating",
  "creating",
]);

export default function products(state = INITIAL_STATE, action) {
  switch (action.type) {
    case REQUEST_PRODUCTS_SUCCESS:
      const payload = action?.payload?.data?.products || [];

      return {
        ...state,
        products: uniqBy(
          [...state.products, ...payload],
          (x) => x?.platformProductId
        ),
      };

    case DELETE_PRODUCTS_SUCCESS:
      const afterDelete = state?.manualProducts.filter(
        (product) => product?.id !== action?.payload?.id
      );

      return {
        ...state,
        manualProducts: afterDelete,
      };

    case REMOVE_LISTINGS_SUCCESS: {
      const removedIds = new Set(action?.payload?.ids || []);
      return {
        ...state,
        products: (state?.products || []).filter(
          (product) => !removedIds.has(product?.id)
        ),
      };
    }

    case DELETE_PRODUCTS_FAILURE:
      return { ...state, error: "Record not found" };

    case REQUEST_AND_RESET_PRODUCTS_SUCCESS:
      const resetPayload = action?.payload?.data?.products || [];

      return {
        ...state,
        products: resetPayload,
        lastId: "",
      };

    case REQUEST_AND_RESET_PRODUCTS_FAILURE:
      return {
        ...state,
        products: [],
        lastId: "",
      };

    // case REQUEST_PRODUCTS_DETAILS_SUCCESS:
    //   let { product = {}, variants } = action?.payload?.data || {};
    //   const variantProperties = (variants?.[0]?.properties || []).map(
    //     (f) => f.name
    //   );

    //   product = {
    //     ...product,
    //     variantProperties,
    //     variants,
    //   };

    //   return {
    //     ...state,
    //     products: uniqBy(
    //       [product, ...state.products],
    //       (x) => x?.platformProductId
    //     ),
    //   };

    case REQUEST_PRODUCTS_FAILURE:
    case REQUEST_PRODUCTS_DETAILS_FAILURE:
      return { ...state, error: "Record not found" };

    case UPDATE_MANUAL_PRODUCT_NAME_SUCCESS: {
      const renamed = action?.payload?.data || action?.payload || {};
      const productId = renamed.id;
      const nextName = renamed.name;
      if (!productId || !nextName) return state;
      return {
        ...state,
        manualProducts: (state.manualProducts || []).map((product) =>
          product?.id === productId ? { ...product, name: nextName } : product
        ),
      };
    }

    case REQUEST_MANUAL_PRODUCT_SUCCESS: {
      let newManualProducts = (action?.payload?.data?.manualProducts || []).filter(
        (product) => !isStoreImportProduct(product)
      );
      const reset = action?.payload?.meta?.reset;
      if (reset) {
        newManualProducts = uniqBy(newManualProducts, (x) => x?.id);
      } else {
        newManualProducts = uniqBy(
          [...state.manualProducts, ...newManualProducts],
          (x) => x?.id
        );
      }

      const catalogUids = action?.payload?.data?.catalogUids;
      return {
        ...state,
        manualProducts: newManualProducts,
        ...(Array.isArray(catalogUids)
          ? { manualProductCatalogUids: catalogUids }
          : {}),
      };
    }

    case REQUEST_BLANK_PRODUCT_COLORS_SUCCESS:
      // Build the color->index map in O(n). The previous version spread the
      // entire accumulated object on every iteration
      // (`newColors = { ...newColors, [color]: index }`), which is O(n^2) and
      // blocked the main thread for ~12s once a tenant's aggregated color list
      // grew large — the dominant cause of slow customizer boot. Mutating the
      // local object produces the identical result in linear time.
      let newColors = {};
      (action.payload?.colors || []).forEach((color, index) => {
        newColors[color] = index;
      });
      return {
        ...state,
        colors: newColors,
      };

    case REQUEST_PRODUCTS_COUNT_SUCCESS:
      let counts = action.payload?.data?.counts || {};
      return {
        ...state,
        counts,
      };

    case REQUEST_PRODUCTS_COUNT_FAILURE:
      return { ...state, error: "Record not found" };

    case SAVE_LISTING_TEMPLATE_SUCCESS:
      const newTemplates = {
        ...state?.templates,
        [action?.payload?.data?.id]: {
          ...(action?.payload?.params?.template || {}),
          id: action?.payload?.data?.id,
        },
      };
      return { ...state, templates: newTemplates };

    case GET_LISTING_TEMPLATE_SUCCESS:
      const templates = keyBy(action?.payload?.templates || [], "id");
      return { ...state, templates };

    case DELETE_LISTING_TEMPLATE_SUCCESS:
      const id = action?.payload?.id;
      const updatedTemplates = omit(state?.templates || {}, [id]);
      return { ...state, templates: updatedTemplates };

    case SAVE_PRODUCT_MAPPING_SUCCESS:
      const updatedProduct = action?.payload?.data;
      const updatedProductList = state?.products.map((p) => {
        if (p?.id === updatedProduct?.id) {
          return updatedProduct;
        }

        return p;
      });

      return { ...state, products: updatedProductList };

    case BULK_IGNORE_PRODUCTS_SUCCESS: {
      // Job-start responses only have jobId/total. List updates use
      // markProductsIgnored({ productIds, ignored }).
      const { productIds, ignored = true } = action?.payload?.data || {};
      if (!Array.isArray(productIds) || !productIds.length) {
        return state;
      }
      const ignoredSet = new Set(productIds);
      return {
        ...state,
        products: state.products.map((p) =>
          ignoredSet.has(p?.id)
            ? {
                ...p,
                ignored: !!ignored,
                mapped: ignored ? "mapped" : p.mapped,
              }
            : p
        ),
      };
    }

    case SET_BULK_IGNORE_JOB:
      return { ...state, bulkIgnoreJob: action.payload || null };

    case CLEAR_BULK_IGNORE_JOB:
      return { ...state, bulkIgnoreJob: null };

    case SET_MOVE_ALL_TO_APP_LOCATION_JOB:
      return { ...state, moveAllToAppLocationJob: action.payload || null };

    case CLEAR_MOVE_ALL_TO_APP_LOCATION_JOB:
      return { ...state, moveAllToAppLocationJob: null };

    case SET_PRODUCT_CREATE_JOB: {
      const job = action.payload || null;
      let manualProducts = state.manualProducts;
      if (
        job?.draftProductId &&
        job?.title &&
        job?.jobId &&
        ACTIVE_CREATE_JOB_STATUSES.has(job.status)
      ) {
        const draft = {
          id: job.draftProductId,
          name: job.title,
          createStatus: "creating",
          pendingCreate: true,
          createJobId: job.jobId,
          skus: [],
          activeShops: [],
          updatedAt: Date.now(),
        };
        manualProducts = uniqBy([draft, ...state.manualProducts], (x) => x?.id);
      }
      return { ...state, productCreateJob: job, manualProducts };
    }

    case CLEAR_PRODUCT_CREATE_JOB:
      return { ...state, productCreateJob: null };

    case PATCH_MANUAL_PRODUCT: {
      const patch = action.payload || {};
      if (!patch.id) return state;
      return {
        ...state,
        manualProducts: (state.manualProducts || []).map((product) =>
          product?.id === patch.id
            ? {
                ...product,
                ...(patch.channels ? { channels: patch.channels } : {}),
                ...(patch.activeShops ? { activeShops: patch.activeShops } : {}),
              }
            : product
        ),
      };
    }

    case UPDATE_PREVIEW_IMAGES:
      return { ...state, previewImages: action?.payload };

    case UPDATE_CANVAS_IMAGES:
      return { ...state, canvasImages: action?.payload };

    case SET_SELECTED_FILTER_FOR_LISTING:
      return { ...state, selectedFilter: action.payload };

    case SET_LISTING_LAST_ID:
      return { ...state, lastId: action.payload };

    default:
      return state;
  }
}
